// GroupForm.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  CircularProgress,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Formik, Form, Field, type FormikProps, type FieldProps, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { EntityPath, Plate } from '../../../types/plate';
import { Group } from '../../../types/group';
import { MultiCheckListField } from '../../multiselect/MultiCheckField2';
import { useFetch } from '../../../hooks/useFetch2';
import { api } from '../../../api/groupApi';
import CheckboxTreeView from '../../tree/CheckboxTreeView_test';
import { FormikRichTreeView } from '../../tree/FormikRichTreeView3';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { makeTreeItems } from '../../tree/tools';
import useSaveWithTranslation from '../../../hooks/useSaveWithTranslation';
import { useAuthStore } from '../../../store/useAuthStore3';

export interface GroupFormProps {
  group: Group | null;
  onSave: (values: Group) => void;
  onCancel: () => void;
  entityPaths: EntityPath[];
}

const GroupForm: React.FC<GroupFormProps> = ({
  group,
  onSave,
  onCancel,
  entityPaths,
}) => {
  const { t } = useTranslation();
  // const title = group ? 'Редагування групи' : 'Нова група';
  const title = group ? t('GroupForm.group_edit') : t('GroupForm.group_new');
  const { customerId } = useAuthStore();
  // const { handleSave, SaveErrorHandler } = useSaveWithTranslation(api.post);

  // useCallback to prevent looping
  const fetchGroupToEntity = React.useCallback(
    () => api.getGroupToEntity(group?.group_id || -1),
    [group?.group_id]
  );
  const { data, setData, error } = useFetch<number[]>(fetchGroupToEntity, []);
  const data_s = data.map(i => String(i));

  const initialValues: Group = group || {
    customer_id: customerId || -1,
    group_id: -1,
    name: '',
    entity_ids: [],
    is_white: false,
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(('group_is_required')),
  });

  if (customerId === -1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{('Loading_form_data')}</Typography>
      </Box>
    );
  }

  const options = entityPaths.map(ep => ({
    value: String(ep.entity_id),
    label: ep.path_names,
  }));

  /*const CompareArray = (a: number[], b: number[]) => {
    for (let i = 0; i < Math.min(a.length, b.length); i++) {
      if (a[i] !== b[i]) {
        return a[i] - b[i];
      }
    }
    return a.length - b.length;
  };

  const makeTreeItems = (entityPaths: EntityPath[]): TreeViewBaseItem[] => {
    const sorted = entityPaths.sort((a, b) => CompareArray(a.path_ids, b.path_ids));
    const treeItems: TreeViewBaseItem[] = [];
    entityPaths.forEach(ep => {

    });
    return treeItems;
  }*/

  const treeItems: TreeViewBaseItem[] = makeTreeItems(entityPaths);
 
  /*
  const treeItems1: TreeViewBaseItem[] = [
    {
      id: '1',
      label: 'Приложения',
      children: [
        { id: '1.1', label: 'Календарь' },
        { id: '1.2', label: 'Заметки' },
      ],
    },
    {
      id: '2',
      label: 'Документы',
      children: [
        { id: '2.1', label: 'Отчеты', children: [{ id: '2.1.1', label: 'Квартальный' }] },
        { id: '2.2', label: 'Презентации' },
      ],
    },
    { id: '3', label: 'Медиа' },

  ];
  */

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        onSave(values);
      }}
  //     onSubmit={(values, { setSubmitting }: FormikHelpers<Group>) => {
  //       handleSave(values, setSubmitting);
  // }   }
      enableReinitialize
    >
      {(formikProps: FormikProps<Group>) => {
        const { values, setFieldValue } = formikProps;

        return (
          <Form>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>

            {/* {SaveErrorHandler} */}

            <Stack spacing={2} sx={{ mt: 2 }}>
              <Field name="name">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('GroupForm.group_name')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
              
              <Field name="is_white" type="checkbox">
                {({ field, meta }: { field: any; meta: any }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label={t('GroupForm.is_white')}
                    />
                  </Box>
                )}
              </Field>

              {false && !!group && (
              <MultiCheckListField 
                name="entity_ids" 
                label={t('GroupForm.Link_to_entity')} 
                // label="Link to entity" 
                options={options} 
                initialSelected={data}
              />)}

              {/* <CheckboxTreeView/> */}

              {!!group && (
              <FormikRichTreeView
                name="entity_ids"
                label={t('GroupForm.Link_to_entity')} 
                // label="Link to entity"
                items={treeItems}
                initialSelectedIds={data_s}
                defaultExpandedItems={['1', '2']}
                selectionPropagation={{ parents: true, descendants: true }}
              />)}

            </Stack>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={onCancel} sx={{ mr: 1 }} disabled={formikProps.isSubmitting} variant='outlined'>
                  {t('OriginForm.Cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty }>
                  {formikProps.isSubmitting ? t('OriginForm.Saving') : t('GroupForm.Save')}
                </Button>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default GroupForm;